import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import { FAQ_DEFAULT_MESSAGES } from "../../utils/faqSkillConfig";

function NestedAccordion({ title, subtitle, children }) {
  return (
    <Accordion
      variant="outlined"
      disableGutters
      sx={{
        "&:before": { display: "none" },
        borderRadius: "10px !important",
        overflow: "hidden",
        borderColor: "divider",
        bgcolor: "action.hover",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon fontSize="small" />}
        sx={{ minHeight: 40, "& .MuiAccordionSummary-content": { my: 0.75 } }}
      >
        <Stack spacing={0.25}>
          <span style={{ fontSize: "0.8125rem", fontWeight: 600, lineHeight: 1.35 }}>{title}</span>
          {subtitle ? (
            <span style={{ fontSize: "0.75rem", color: "var(--mui-palette-text-secondary)", lineHeight: 1.35 }}>
              {subtitle}
            </span>
          ) : null}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, pb: 2 }}>{children}</AccordionDetails>
    </Accordion>
  );
}

export default function FaqMessagesConfigSection({ skillConfig, disabled, onChange }) {
  const { t } = useTranslation("settings");
  const cfg = skillConfig || {};

  const merge = (partial) => onChange?.(partial);

  return (
    <Stack spacing={2}>
      <Alert severity="info" icon={false} sx={{ py: 1, "& .MuiAlert-message": { width: "100%" } }}>
        {t("agentsListPage.faqMessagesOptionalNote")}
      </Alert>

      <TextField
        label={t("agentsListPage.faqGreetingMessage")}
        helperText={t("agentsListPage.faqGreetingMessageHelper")}
        placeholder={FAQ_DEFAULT_MESSAGES.greeting_message}
        value={cfg.greeting_message || ""}
        onChange={(e) => merge({ greeting_message: e.target.value })}
        multiline
        minRows={2}
        fullWidth
        size="small"
        disabled={disabled}
      />

      <TextField
        label={t("agentsListPage.faqAssistantIdentity")}
        helperText={t("agentsListPage.faqAssistantIdentityHelper")}
        placeholder={t("agentsListPage.faqAssistantIdentityPlaceholder")}
        value={cfg.assistant_identity || ""}
        onChange={(e) => merge({ assistant_identity: e.target.value })}
        fullWidth
        size="small"
        disabled={disabled}
      />

      <FormControlLabel
        control={
          <Switch
            checked={cfg.avoid_closing_questions === true}
            onChange={(e) => merge({ avoid_closing_questions: e.target.checked })}
            disabled={disabled}
          />
        }
        label={t("agentsListPage.faqAvoidClosingQuestions")}
      />

      <NestedAccordion
        title={t("agentsListPage.faqMessagesAdvancedTitle")}
        subtitle={t("agentsListPage.faqMessagesAdvancedSubtitle")}
      >
        <Stack spacing={2}>
          <TextField
            label={t("agentsListPage.faqGreetingMorning")}
            helperText={t("agentsListPage.faqGreetingTimeHelper")}
            placeholder={FAQ_DEFAULT_MESSAGES.greeting_message_morning}
            value={cfg.greeting_message_morning || ""}
            onChange={(e) => merge({ greeting_message_morning: e.target.value })}
            multiline
            minRows={2}
            fullWidth
            size="small"
            disabled={disabled}
          />

          <TextField
            label={t("agentsListPage.faqGreetingAfternoon")}
            helperText={t("agentsListPage.faqGreetingTimeHelper")}
            placeholder={FAQ_DEFAULT_MESSAGES.greeting_message_afternoon}
            value={cfg.greeting_message_afternoon || ""}
            onChange={(e) => merge({ greeting_message_afternoon: e.target.value })}
            multiline
            minRows={2}
            fullWidth
            size="small"
            disabled={disabled}
          />

          <TextField
            label={t("agentsListPage.faqGreetingEvening")}
            helperText={t("agentsListPage.faqGreetingTimeHelper")}
            placeholder={FAQ_DEFAULT_MESSAGES.greeting_message_evening}
            value={cfg.greeting_message_evening || ""}
            onChange={(e) => merge({ greeting_message_evening: e.target.value })}
            multiline
            minRows={2}
            fullWidth
            size="small"
            disabled={disabled}
          />

          <TextField
            label={t("agentsListPage.faqNoMatchReply")}
            helperText={t("agentsListPage.faqNoMatchReplyHelper")}
            placeholder={FAQ_DEFAULT_MESSAGES.no_match_reply}
            value={cfg.no_match_reply || ""}
            onChange={(e) => merge({ no_match_reply: e.target.value })}
            multiline
            minRows={2}
            fullWidth
            size="small"
            disabled={disabled}
          />

          <TextField
            label={t("agentsListPage.faqTransferOfferHint")}
            helperText={t("agentsListPage.faqTransferOfferHintHelper")}
            value={cfg.transfer_offer_hint || ""}
            onChange={(e) => merge({ transfer_offer_hint: e.target.value })}
            multiline
            minRows={2}
            fullWidth
            size="small"
            disabled={disabled}
          />
        </Stack>
      </NestedAccordion>
    </Stack>
  );
}
